<script>
    import {
        CardLayout,
        NotificationModal,
    } from "../../../Components/Common/index.svelte";

    import {
        amaUnitValue,
        asaAllItemsList,
        mbsAllItemsList,
    } from "../../../stores";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        addHoursToDate,
        getAmountInCurrency,
        getBillingType,
        getBoolean,
        getFormatDateAndTime,
        getInvoiceReason,
        getOOPType,
        goToDownloadPage,
        isObjectEmpty,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        getDescriptionByTime,
        getTimeItemsByUnits,
        getTimeUnits,
        updateProcedureItems as updateAsaProcedureItems,
    } from "../helpers/asaHelper.svelte";

    import {
        convertTimeRangeInMin,
        convertTitleToMin,
        updateProcedureItems as updateMbsProcedureItems,
    } from "../helpers/mbsHelper.svelte";

    import {
        syncInsuranceData,
        updateEncounterLatestBillingData,
        enablePatientClaimForEncounter
    } from "../helpers/encounterHelper.svelte";

    import PatientEncounterBillingDetailsModal from "./PatientEncounterBillingDetailsModal.svelte";

    import {
        addIcon,
        editIcon,
        insuranceTypesMayAllowAmaBillingRule,
    } from "../../../Utils/utilConstants.svelte";

    import { getPublicHolidays } from "../../../Utils/DataLoaders.svelte";
    import InfoIcon from "../../../Components/Common/InfoIcon.svelte";

    export let patientEncounterDetails = {};
    export let billingDetails = {};
    export let latestObjDetails = {};
    export let latestQuoteObjDetails = {};
    export let itemsData = [];
    export let associateArr = [];
    export let type = "";
    export let scenarioOneFulfilled = false;
    export let scenarioTwoFulfilled = false;
    export let reloadBillingDetailsAndUpdateDefaultItems = () => {};
    export let reloadBillingItemsWhenTimeItemAdded = () => {};
    export let reloadBillingDetails = () => {};
    export let regenerateQuoteOrInvoice = () => {};

    $: isFromQuote = latestObjDetails.isLatestQuoteData;

    $: isEncounterCompleted = billingDetails.billingStatus == 5;

    $: btnOptions = [
        {
            iconName: "editIcon",
            isHidden: true,
            iconFunction: () => _openEditDetailModal(),
            hasTooltip: false,
            iFont: isObjectEmpty(latestObjDetails) ? addIcon : editIcon,
            btnText: isObjectEmpty(latestObjDetails) ? "Add" : "Edit",
        },
    ];

     $: showPatientClaimIndication = enablePatientClaimForEncounter(
        patientEncounterDetails,
    );

    $: showDownloadPatientClaimDocument =
        latestObjDetails.patientClaim &&
        patientEncounterDetails.patient?.insuranceType == 0;

    /* 
        show "Create Invoice and Claim Date" field in Invoice Billing Details section if:
        1. encounter billing status == 3 (Submitted)
        2. has NO active invoices
        3. Date for creating the invoice is today or future date
        4. latest invoice details OOP type is Post-Pay
    */
    $: showCreateInvoiceAndClaimDateField =
        type?.toLowerCase() == "invoice" &&
        patientEncounterDetails &&
        patientEncounterDetails.billingStatus == 3 &&
        !isObjectEmpty(latestObjDetails) &&
        !hasValidAssociateQuoteOrInvoice &&
        _isClaimDateTodayOrFuture(
            addHoursToDate(
                patientEncounterDetails.updatedDate,
                patientEncounterDetails.claimDelayHours,
            ),
        ) &&
        latestObjDetails.oopType == 1;

    /* 
        show "invoice custom due date" field in Invoice Billing Details under "Create Invoice and Claim Date" if:
        1. encounter billing status < 4
        2. has NO active invoices
        3. billingData has customDueDate
    */
    $: showInvoiceCustomDueDateField =
        showCreateInvoiceAndClaimDateField && latestObjDetails?.customDueDate;

    $: hasValidAssociateQuoteOrInvoice =
        associateArr?.length && associateArr.some((data) => data.status != 9);

    let isEditDetailsModalOpen = false,
        isUpdatingDetails = false,
        isTimeItemAdded = false,
        isProcedureItemAdded = false;

    let isSyncAssociateQuoteOrInvoiceModalOpen = false,
        isUpdatingAssociateQuoteOrInvoice = false;

    let isConfirmActionModalOpen = false,
        confirmAction = () => {};

    const confirmUpdateLatestDetails = (
        hasBillingTypeFiguresChanged = false,
        ...args
    ) => {
        if (hasBillingTypeFiguresChanged) _openConfirmActionModal(...args);
        else updateLatestDetails(...args);
    };

    const updateLatestDetails = async (
        modalBillingData,
        insuranceObj,
        hasItemTypeChanged = false,
        hasInsuranceTypeChange = false,
    ) => {
        const isQuote = type.toString().toLowerCase() == "quote";

        const tempInsuranceData = syncInsuranceData(
            modalBillingData,
            insuranceObj,
            patientEncounterDetails,
        );

        const { modalData, encounterData } = tempInsuranceData;

        modalBillingData = modalData;

        patientEncounterDetails = encounterData;

        modalBillingData = await _syncBillingDataItemsByDate(modalBillingData);

        modalBillingData = await _syncProcedureItems(modalBillingData);

        _syncBillingData(modalBillingData, isQuote);

        if (hasItemTypeChanged)
            _syncBillingDataItems(modalBillingData, isQuote);

        if (hasInsuranceTypeChange)
            _syncBillingSelectorDetails(modalBillingData);

        if (
            !isFromQuote &&
            modalBillingData.startDate &&
            modalBillingData.endDate
        )
            patientEncounterDetails.billingStatus =
                patientEncounterDetails.billingStatus != 3
                    ? patientEncounterDetails.billingStatus
                    : 2;

        patientEncounterDetails = updateEncounterLatestBillingData(
            patientEncounterDetails,
        );

        // re-calculate whenever latest billing data update
        if (modalBillingData.id && !hasItemTypeChanged)
            await regenerateQuoteOrInvoice(modalBillingData, true, type);

        isUpdatingDetails = true;

        await apiMethods.putMethod
            .updatePatientEncounter(patientEncounterDetails, false, "Update latest details")
            .then(
                async (res) =>
                    await _handleUpdateBillingData(
                        res,
                        hasItemTypeChanged,
                        modalBillingData,
                    ),
            )
            .catch((err) => showException(err));

        isUpdatingDetails = false;

        _closeEditDetailModal();
    };

    const syncAssociateQuoteOrInvoice = async () => {
        isUpdatingAssociateQuoteOrInvoice = true;

        await regenerateQuoteOrInvoice(latestObjDetails, true, type);

        _closeSyncAssociateQuoteOrInvoiceModal(false);

        isUpdatingAssociateQuoteOrInvoice = false;
    };

    const confirmChangeBillingData = () => {
        confirmAction();
        _closeConfirmActionModal();
    };

    const handleDownloadPatientClaimDocument = () => goToDownloadPage(15, 0);

    // HELPER FUNCTIONS
    const _syncBillingData = (data, isQuote) => {
        if (data.id)
            patientEncounterDetails.billingData =
                patientEncounterDetails.billingData.map((bd) => {
                    if (bd.id == data.id) return { ...bd, ...data };
                    return bd;
                });
        else {
            data.isLatestQuoteData = Boolean(isQuote);
            data.isLatestInvoiceData = Boolean(!isQuote);

            if (
                insuranceTypesMayAllowAmaBillingRule.includes(
                    data.insuranceType,
                )
            ) {
                data.billingType = 2;
                data.calculationAmount = $amaUnitValue;
            }

            patientEncounterDetails.billingData = [{ ...data }];
        }
    };

    const _syncBillingDataItemsByDate = async (data) => {
        let res;

        const { useAsaCodes } = data;

        const { typeOfBill } = patientEncounterDetails;

        // ONLY Anaesthesia encounter support time code
        if (typeOfBill != 0) return data;

        if (useAsaCodes) res = _syncAsaTimeItem(data);
        else res = await _syncMbsTimeItem(data);

        return res;
    };

    const _syncProcedureItems = async (data) => {
        let res;

        const { useAsaCodes, startDate, endDate } = data;

        const { typeOfBill, neuraxialType } = patientEncounterDetails;

        if (typeOfBill != 2) return data;

        if (neuraxialType && (!startDate || !endDate)) return data;

        if (useAsaCodes) res = await _syncAsaProcedureItem(data);
        else res = await _syncMbsProcedureItem(data);

        return res;
    };

    const _syncMbsTimeItem = async (data) => {
        const { startDate, endDate, mbsData } = data;

        if (!startDate || !endDate) return { ...data };

        const timeDiff = convertTimeRangeInMin(startDate, endDate);

        let timeItemsList = [];

        if (!$mbsAllItemsList.length)
            await apiMethods.getMethod
                .getMBSItems(0, 999999, {
                    "filter[infoType]": "Time",
                })
                .then((res) => (timeItemsList = [...res.results]));
        else
            timeItemsList = $mbsAllItemsList.filter(
                (item) => item.infoType == "Time",
            );

        const targetTimeItem = timeItemsList.find((item) => {
            const timeRange = convertTitleToMin(item.infoShortTitle);
            return timeDiff >= timeRange[0] && timeDiff <= timeRange[1];
        });

        if (!targetTimeItem) return { ...data };

        if (!mbsData) {
            isTimeItemAdded = true;
            return { ...data, mbsData: [{ ...targetTimeItem }] };
        }

        const currentTimeData = mbsData.find((item) => item.infoType == "Time");

        if (
            currentTimeData &&
            currentTimeData.infoShortTitle == targetTimeItem.infoShortTitle
        )
            return { ...data };

        const newMbsData = mbsData.filter((item) => item.infoType != "Time");

        isTimeItemAdded = true;

        return { ...data, mbsData: [...newMbsData, { ...targetTimeItem }] };
    };

    const _syncAsaTimeItem = (data) => {
        const { startDate, endDate, usingAmaRates, calculationAmount } = data;

        if (!startDate || !endDate) return { ...data };

        const timeDiff = convertTimeRangeInMin(startDate, endDate);
        const targetTimeItemIndex = _getTimeItemIndex(timeDiff);
        const targetTimeItemDescription =
            getDescriptionByTime(targetTimeItemIndex);

        const isWcOrTacInsurance =
            insuranceTypesMayAllowAmaBillingRule.includes(data.insuranceType);

        const shouldApplyDynamicFeeCharge = isWcOrTacInsurance && usingAmaRates;

        const updateTimeItem = {
            units: getTimeUnits(targetTimeItemIndex),
            feeCharged:
                getTimeUnits(targetTimeItemIndex) *
                (shouldApplyDynamicFeeCharge
                    ? calculationAmount > 0
                        ? calculationAmount
                        : $amaUnitValue
                    : $amaUnitValue),
            encounterItemDescription: targetTimeItemDescription,
            itemCode: "Time",
        };

        const timeItem = itemsData.find((item) => item.itemCode == "Time");

        if (!timeItem) {
            isTimeItemAdded = true;
            return { ...data, asaTimeData: [{ ...updateTimeItem }] };
        } else {
            const timeItemIndex = getTimeItemsByUnits(itemsData);

            const timeItemDescription = getDescriptionByTime(timeItemIndex);

            if (timeItemDescription == targetTimeItemDescription)
                return { ...data };

            isTimeItemAdded = true;

            return { ...data, asaTimeData: [{ ...updateTimeItem }] };
        }
    };

    const _syncMbsProcedureItem = async (data) => {
        const { mbsData, startDate, endDate } = data;
        const { neuraxialType, typeOfBill, scheduledDate } =
            patientEncounterDetails;
        const targetProcedureItem =
            neuraxialType == 1
                ? { key: 1, value: "Insertion" }
                : { key: 2, value: "Top up | Revision" };
        const holidayObj = {
            "filter[year]": scheduledDate
                ? new Date(scheduledDate).getFullYear()
                : new Date().getFullYear(),
        };
        const itemProps = {
            startDate,
            endDate,
            billingType: typeOfBill,
        };

        let res = mbsData ?? [],
            procedureItems = [],
            holidaysInState = [];

        if (!$mbsAllItemsList.length)
            await apiMethods.getMethod
                .getMBSItems(0, 999999, {
                    "filter[infoType]": "AdditionalProcedures",
                })
                .then((res) => (procedureItems = [...res.results]));
        else
            procedureItems = $mbsAllItemsList.filter(
                (item) => item.infoType == "AdditionalProcedures",
            );

        if (data.patientInsuranceState)
            holidayObj["filter[state]"] = data.patientInsuranceState;

        await getPublicHolidays(0, 999999, holidayObj).then(
            (res) => (holidaysInState = [...res.results] ?? []),
        );

        res = await updateMbsProcedureItems(
            targetProcedureItem,
            res,
            itemProps,
            procedureItems,
            holidaysInState,
        );

        isProcedureItemAdded = true;

        return { ...data, mbsData: [...res] };
    };

    const _syncAsaProcedureItem = async (data) => {
        const { asaData, startDate, endDate } = data;
        const { neuraxialType, typeOfBill } = patientEncounterDetails;
        const targetProcedureItem =
            neuraxialType == 1
                ? { key: 1, value: "Insertion" }
                : { key: 2, value: "Top up | Revision" };
        const itemProps = {
            startDate,
            endDate,
            billingType: typeOfBill,
        };

        let res = asaData ?? [],
            procedureItems = [];

        if (!$asaAllItemsList.length)
            await apiMethods.getMethod
                .getASAItems(0, 999999, {
                    "filter[infoType]": "AdditionalProcedures",
                })
                .then((res) => (procedureItems = [...res.results]));
        else
            procedureItems = $asaAllItemsList.filter(
                (item) => item.infoType == "AdditionalProcedures",
            );

        res = await updateAsaProcedureItems(
            targetProcedureItem,
            res,
            itemProps,
            procedureItems,
        );

        isProcedureItemAdded = true;

        return { ...data, asaData: [...res] };
    };

    const _getTimeItemIndex = (timeDiff) => {
        if (timeDiff <= 120) return Math.ceil(timeDiff / 15) - 1;
        else if (timeDiff <= 240)
            return 8 + Math.ceil((timeDiff - 120) / 10) - 1;
        else return 20 + Math.ceil((timeDiff - 240) / 5) - 1;
    };

    const _syncBillingDataItems = (data, isQuote) => {
        patientEncounterDetails.billingData =
            patientEncounterDetails.billingData.map((bd) => {
                if (
                    bd.id == data.id &&
                    ((isQuote && bd.isLatestQuoteData) ||
                        (!isQuote && bd.isLatestInvoiceData))
                )
                    return {
                        ...bd,
                        asaData: [],
                        mbsData: [],
                        asaTimeData: null,
                        actualGap: Number(0),
                        rebate: Number(0),
                        totalFee: Number(0),
                        useAsaCodes:
                            bd.id == data.id ? bd.useAsaCodes : !bd.useAsaCodes, // avoid useAsaCode change twice and remain as before
                        usingAmaRates: bd.useAsaCodes,
                    };
                else return { ...bd };
            });
    };

    const _syncBillingSelectorDetails = (data) => {
        patientEncounterDetails.billingData =
            patientEncounterDetails.billingData.map((bd) => {
                if (data.id) {
                    if (bd.id == data.id)
                        return {
                            ...bd,
                            billingType: Number(0),
                            calculationAmount: Number(0),
                            actualGap: Number(0),
                            totalFee: Number(0),
                            rebate: Number(0),
                            oopType: Number(0),
                            // withinFundRules: false,
                            // patientPaysTotalFee: false,
                        };
                    else return { ...bd };
                } else if (data.isLatestQuoteData || data.isLatestInvoiceData)
                    return { ...bd, ...data };
                else return { ...bd };
            });
    };

    const _handleUpdateBillingData = async (
        res,
        hasItemTypeChanged,
        updatedBillingData,
    ) => {
        if (!res)
            showError(
                `Failed to update ${type} billing details, please refresh the page and try again and contact support if the issue persists.`,
            );
        else if (res.error) showError(res.error);
        else if (!associateArr || !associateArr.length) {
            if (hasItemTypeChanged)
                await reloadBillingDetailsAndUpdateDefaultItems();
            else if (isTimeItemAdded || isProcedureItemAdded)
                await reloadBillingItemsWhenTimeItemAdded(type);
            else if (res.id == patientEncounterDetails.id)
                await reloadBillingDetails();
        } else {
            if (
                associateArr.some(
                    (data) =>
                        data.patientEncounterBillingDataId ==
                        updatedBillingData.id,
                )
            )
                _openSyncAssociateQuoteOrInvoiceModal(
                    updatedBillingData,
                    hasItemTypeChanged,
                );
            else if (hasItemTypeChanged)
                await reloadBillingDetailsAndUpdateDefaultItems();
            else if (isTimeItemAdded || isProcedureItemAdded)
                await reloadBillingItemsWhenTimeItemAdded(type);
            else if (res.id == patientEncounterDetails.id)
                await reloadBillingDetails();
        }

        isTimeItemAdded = false;
        isProcedureItemAdded = false;
    };

    const _openEditDetailModal = () => (isEditDetailsModalOpen = true);

    const _closeEditDetailModal = () => {
        isEditDetailsModalOpen = false;
    };

    const _openSyncAssociateQuoteOrInvoiceModal = (
        data,
        hasItemTypeChanged,
    ) => {
        latestObjDetails = { ...data, hasItemTypeChanged };

        if (hasItemTypeChanged) {
            latestObjDetails.mbsData = [];
            latestObjDetails.asaData = [];
        }
        isSyncAssociateQuoteOrInvoiceModalOpen = true;
    };

    const _closeSyncAssociateQuoteOrInvoiceModal = (needReload = true) => {
        isSyncAssociateQuoteOrInvoiceModalOpen = false;

        if (!needReload) return;

        if (latestObjDetails.hasItemTypeChanged)
            reloadBillingDetailsAndUpdateDefaultItems();
        else reloadBillingDetails();
    };

    const _openConfirmActionModal = (...args) => {
        if (!scenarioOneFulfilled) {
            updateLatestDetails(...args);
            _closeConfirmActionModal();
            return;
        }

        isConfirmActionModalOpen = true;

        confirmAction = () => updateLatestDetails(...args);
    };

    const _closeConfirmActionModal = () => {
        isConfirmActionModalOpen = false;
        confirmAction = () => {};
    };

    const _getCreateInvoiceAndClaimDateLabel = () => {
        const { insuranceType, patientPaysTotalFee } = latestObjDetails;

        // // Private, DVA, or Self-insured with patient NOT pays total fee
        // if (
        //     [0, 20].includes(insuranceType) ||
        //     (insuranceType == 11 && !patientPaysTotalFee)
        // )
        //     return "Create Invoice and Claim On:";

        // return "Create Invoice On:";
        return patientPaysTotalFee
            ? "Create Invoice On:"
            : "Create Invoice and Claim On:";
    };

    const _isClaimDateTodayOrFuture = (dateStr) => {
        const claimDate = new Date(dateStr);
        const currDate = new Date();

        claimDate.setHours(0, 0, 0, 0);
        currDate.setHours(0, 0, 0, 0);

        if (claimDate > currDate) return true;
        else if (claimDate.toDateString() == currDate.toDateString()) {
            const claimTime = new Date(dateStr);
            const currTime = new Date();

            return claimTime >= currTime;
        } else return false;
    };
</script>

<CardLayout title={`${type} Billing Details`} {btnOptions}>
    {#if isObjectEmpty(latestObjDetails)}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <p>
                    No {type.toString().toLowerCase()} billing data
                </p>
            </div>
        </div>
    {:else}
        <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
           
            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">OOP Type:</p>
                    <p class="content">
                        {getOOPType(latestObjDetails.oopType)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Patient Pays Total Fee:</p>
                    <p class="content">
                        <span >
                            {getBoolean(latestObjDetails.patientPaysTotalFee)} 
                        </span>
                        {#if latestObjDetails.patientPaysTotalFee && latestObjDetails.patientClaim}
                            <span class={`cursor:pointer tooltip tooltip-secondary underline`}  data-tip={"Patient Claim Interactive (PCI) allows you to submit a claim on behalf of the patient, so the rebate from a total fee invoice is paid directly to the patient."}>
                                (Patient claim)
                            </span>
                        {/if}
                    </p>
                </div>
                {#if latestObjDetails.patientPaysTotalFee && showDownloadPatientClaimDocument}
                    <div class="flex flex-row gap-x-2">
                    <p class="title"></p>
                    <p
                        class="content pt-1 text-sm cursor-pointer underline hover:font-bold"
                        on:click={handleDownloadPatientClaimDocument}
                    >
                        Download Patient Claim Consent and Declaration
                    </p>
                    </div>
                {/if}

                <div class="flex flex-row gap-x-2">
                    <p class="title">Within Fund Rules:</p>
                    <p class="content">
                        {getBoolean(latestObjDetails.withinFundRules)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Billing Type:</p>
                    <p class="content">
                        {getBillingType(latestObjDetails.billingType ?? 0)}
                    </p>
                </div>

                {#if type.toLowerCase() == "invoice"}
                    <div class="flex flex-row gap-x-2">
                        <p class="title">Reason:</p>
                        <p class="content">
                            {getInvoiceReason(latestObjDetails.invoiceReason)}
                        </p>
                    </div>
                {/if}

                <!-- <div class="flex flex-row gap-x-2" >
                    <p class="title">Patient Claim:</p>
                    <p class="content">
                        <span class="cursor:pointer tooltip tooltip-secondary underline"  data-tip={"Patient claim can be changed on rule selection"}>
                            {getBoolean(patientEncounterDetails.patientClaim)}
                        </span>
                    </p>
                    <input
                        type="checkbox"
                        id="patientClaimToggle"
                        class="toggle toggle-secondary"
                        checked={patientEncounterDetails.patientClaim}
                        disabled={isUpdatingPatientClaim || isEncounterCompleted}
                        on:click|preventDefault={handleTogglePatientClaim}
                    /> 
                </div> -->
                
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">Add GST:</p>
                    <p class="content">
                        {getBoolean(latestObjDetails.addGST)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Actual Gap:</p>
                    <p class="content">
                        {getAmountInCurrency(latestObjDetails.actualGap)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Rebate:</p>
                    <p class="content">
                        {getAmountInCurrency(latestObjDetails.rebate)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Total Fee:</p>
                    <p class="content">
                        {getAmountInCurrency(
                            latestObjDetails.addGST
                                ? latestObjDetails.totalFee * 1.1
                                : latestObjDetails.totalFee,
                        )}
                    </p>
                </div>
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">Start Date:</p>
                    <p class="content">
                        {getFormatDateAndTime(latestObjDetails.startDate)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">End Date:</p>
                    <p class="content">
                        {getFormatDateAndTime(latestObjDetails.endDate)}
                    </p>
                </div>

                {#if showCreateInvoiceAndClaimDateField}
                    <div class="flex flex-row gap-x-2">
                        <p class="title">
                            {_getCreateInvoiceAndClaimDateLabel()}
                        </p>
                        <p class="content">
                            {getFormatDateAndTime(
                                addHoursToDate(
                                    patientEncounterDetails.updatedDate,
                                    patientEncounterDetails.claimDelayHours,
                                ),
                            )}
                        </p>
                    </div>
                {/if}

                {#if showInvoiceCustomDueDateField}
                    <div class="flex flex-row gap-x-2">
                        <p class="title">
                        <InfoIcon info="The due date for the invoice to be paid" ></InfoIcon>
                            Invoice Custom Due Date:
                        </p>
                        <p class="content">
                            {getFormatDateAndTime(
                                latestObjDetails.customDueDate,
                            )}
                        </p>
                    </div>
                {/if}
            </div>
        </div>

        
    {/if}
</CardLayout>

<PatientEncounterBillingDetailsModal
    isModalOpen={isEditDetailsModalOpen}
    {latestObjDetails}
    {latestQuoteObjDetails}
    {patientEncounterDetails}
    updateLatestDetails={confirmUpdateLatestDetails}
    {itemsData}
    closeModal={_closeEditDetailModal}
    {type}
    isUpdatingBillingDetails={isUpdatingDetails}
/>

<NotificationModal
    isModalOpen={isSyncAssociateQuoteOrInvoiceModalOpen}
    submitText="Confirm"
    closeModal={_closeSyncAssociateQuoteOrInvoiceModal}
    confirmAction={syncAssociateQuoteOrInvoice}
    isConfirming={isUpdatingAssociateQuoteOrInvoice}
    closeModalFunction={_closeSyncAssociateQuoteOrInvoiceModal}
>
    <p class="leading-6">
        Update successfully. Please note this change will NOT affect the
        existing {type.toString().toLowerCase() == "quote"
            ? "quote"
            : "invoice"}.
    </p>

    <p class="leading-6">
        Do you want to re-calculate the totals and re-generate the existing {type
            .toString()
            .toLowerCase() == "quote"
            ? "quote"
            : "invoice"} and create & send a {type} of {getAmountInCurrency(
            latestObjDetails.totalFee,
        )} with a gap of {getAmountInCurrency(latestObjDetails.actualGap)} to the
        patient??
    </p>
</NotificationModal>

<NotificationModal
    title="Notice"
    isModalOpen={isConfirmActionModalOpen}
    closeModal={_closeConfirmActionModal}
    confirmAction={confirmChangeBillingData}
    cancelText="Cancel"
    submitText="Confirm"
    closeModalFunction={_closeConfirmActionModal}
>
    <p>
        {#if scenarioTwoFulfilled}
            A paid {type.toLowerCase()} already exists. Updating the billing information
            will cancel the original {type.toLowerCase()}, create a new one and
            move all the payments to it.
        {:else}
            An unpaid {type.toLowerCase()} already exists. Updating the billing information
            will also update and regenerate the associated {type.toLowerCase()}.
        {/if}
    </p>

    <p class="leading-6">Do you want to proceed?</p>
</NotificationModal>

<style>
    .title {
        width: 200px;
    }

    .content {
        width: calc(100% - 200px);
    }
</style>
